import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { formatApiErrorDetail } from '../../utils/axios';
import { AdminSidebar } from './AdminLeaderboardWinners';
import { LogoUploader } from '../../components/LogoUploader';
import { Save, Settings as SettingsIcon, ExternalLink } from 'lucide-react';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminSettings = () => {
  const [form, setForm] = useState({
    website_name: '',
    logo: '',
    header_logo: '',
    footer_logo: '',
    favicon: '',
    logo_link_url: '',
    hero_title: '',
    hero_subtitle: '',
    hero_background: '',
    countdown_date: '',
    primary_button_text: '',
    secondary_button_text: '',
    rundown_title: '',
    rundown_subtitle: '',
    partners_section_title: '',
    partners_section_subtitle: '',
    stat_matches_label: '',
    stat_participants_label: '',
    stat_predictions_label: '',
    stat_prizes_label: '',
    total_prize_count: '',
    stat_matches_icon: '',
    stat_prizes_icon: '',
    footer_description: '',
    footer_text: '',
    footer_menu_title: '',
    footer_info_title: '',
    terms_content: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/admin/settings`, { withCredentials: true });
      setForm({
        website_name: data.website_name || '',
        logo: data.logo || '',
        header_logo: data.header_logo || '',
        footer_logo: data.footer_logo || '',
        favicon: data.favicon || '',
        logo_link_url: data.logo_link_url || '',
        hero_title: data.hero_title || '',
        hero_subtitle: data.hero_subtitle || '',
        hero_background: data.hero_background || '',
        countdown_date: data.countdown_date ? data.countdown_date.slice(0, 16) : '',
        primary_button_text: data.primary_button_text || '',
        secondary_button_text: data.secondary_button_text || '',
        rundown_title: data.rundown_title || '',
        rundown_subtitle: data.rundown_subtitle || '',
        partners_section_title: data.partners_section_title || '',
        partners_section_subtitle: data.partners_section_subtitle || '',
        stat_matches_label: data.stat_matches_label || '',
        stat_participants_label: data.stat_participants_label || '',
        stat_predictions_label: data.stat_predictions_label || '',
        stat_prizes_label: data.stat_prizes_label || '',
        total_prize_count: data.total_prize_count || '',
        stat_matches_icon: data.stat_matches_icon || '',
        stat_prizes_icon: data.stat_prizes_icon || '',
        footer_description: data.footer_description || '',
        footer_text: data.footer_text || '',
        footer_menu_title: data.footer_menu_title || '',
        footer_info_title: data.footer_info_title || '',
        terms_content: data.terms_content || '',
      });
    } catch (e) {
      toast.error('Gagal memuat pengaturan');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSettings(); }, []);

  const handleSave = async (openPreview = false) => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.countdown_date) {
        payload.countdown_date = new Date(payload.countdown_date).toISOString();
      } else {
        delete payload.countdown_date;
      }
      await axios.put(`${API_BASE}/admin/settings`, payload, { withCredentials: true });
      toast.success(openPreview ? 'Tersimpan! Membuka preview...' : 'Pengaturan berhasil disimpan');
      if (openPreview) {
        // Open homepage in new tab so admin can immediately see the result
        window.open(`${window.location.origin}/?_t=${Date.now()}`, '_blank', 'noopener');
        setSaving(false);
      } else {
        setTimeout(() => window.location.reload(), 800);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Gagal menyimpan');
      setSaving(false);
    }
  };

  const openPreviewNow = () => {
    window.open(`${window.location.origin}/?_t=${Date.now()}`, '_blank', 'noopener');
  };

  return (
    <div className="min-h-screen bg-[#0B1020]" data-testid="admin-settings-page">
      <AdminSidebar active="/admin/settings" />

      <main className="lg:ml-64 p-4 lg:p-8 pt-20 lg:pt-8">
        <div className="mb-8 flex items-center justify-between flex-wrap gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl md:text-4xl font-black text-white mb-2 flex items-center gap-3">
              <SettingsIcon className="text-[#FFD60A] shrink-0" size={28} /> <span className="truncate">Pengaturan Website</span>
            </h1>
            <p className="text-gray-400 text-sm md:text-base">Kelola logo, favicon, hero, dan Syarat &amp; Ketentuan</p>
          </div>
          <div className="flex items-center gap-2 md:gap-3 flex-wrap w-full md:w-auto">
            <button
              onClick={openPreviewNow}
              className="btn-secondary flex items-center gap-2 text-sm px-4 py-2 md:px-6"
              data-testid="preview-homepage-btn"
              title="Buka homepage di tab baru"
            >
              <ExternalLink size={16} /> <span className="hidden sm:inline">Lihat</span> Preview
            </button>
            <button
              onClick={() => handleSave(true)}
              disabled={saving}
              className="btn-secondary flex items-center gap-2 text-sm px-4 py-2 md:px-6"
              data-testid="save-and-preview-btn"
              title="Simpan lalu langsung buka preview di tab baru"
            >
              <ExternalLink size={16} /> {saving ? 'Menyimpan...' : 'Simpan & Preview'}
            </button>
            <button onClick={() => handleSave(false)} disabled={saving} className="btn-primary flex items-center gap-2 text-sm px-4 py-2 md:px-6" data-testid="save-settings-btn">
              <Save size={16} /> {saving ? 'Menyimpan...' : 'Simpan'}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-gray-400">Loading...</div>
        ) : (
          <div className="space-y-6 max-w-4xl">
            <div className="glass-card p-6">
              <h2 className="text-xl font-bold text-white mb-4">Identitas Website</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-white text-sm font-semibold mb-2">Nama Website</label>
                  <input value={form.website_name} onChange={(e) => setForm({...form, website_name: e.target.value})} placeholder="EXTRA TIME" data-testid="website-name-input" />
                </div>
                <LogoUploader
                  value={form.logo}
                  onChange={(url) => setForm({...form, logo: url})}
                  folder="logos"
                  label="Logo Utama Website (fallback untuk header & footer bila khusus tidak diisi)"
                  dataTestId="site-logo"
                />
                <LogoUploader
                  value={form.header_logo}
                  onChange={(url) => setForm({...form, header_logo: url})}
                  folder="logos"
                  label="Logo Header (opsional, override logo utama di navbar atas)"
                  dataTestId="header-logo"
                />
                <LogoUploader
                  value={form.footer_logo}
                  onChange={(url) => setForm({...form, footer_logo: url})}
                  folder="logos"
                  label="Logo Footer (opsional, override logo utama di footer)"
                  dataTestId="footer-logo"
                />
                <LogoUploader
                  value={form.favicon}
                  onChange={(url) => setForm({...form, favicon: url})}
                  folder="logos"
                  label="Favicon (icon di tab browser, disarankan .png / .ico ukuran kecil)"
                  dataTestId="site-favicon"
                />
                <div>
                  <label className="block text-white text-sm font-semibold mb-2">Link Logo &amp; Nama Website (opsional)</label>
                  <input
                    type="url"
                    value={form.logo_link_url}
                    onChange={(e) => setForm({...form, logo_link_url: e.target.value})}
                    placeholder="https://contoh.com — kosongkan untuk arahkan ke Beranda"
                    data-testid="logo-link-url-input"
                  />
                  <p className="text-gray-500 text-xs mt-1">Kalau diisi, logo &amp; nama website di header akan mengarah ke URL ini (buka tab baru). Kosongkan untuk pakai default (Beranda).</p>
                </div>
              </div>
            </div>

            <div className="glass-card p-6">
              <h2 className="text-xl font-bold text-white mb-4">Hero (Bagian Atas Homepage)</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-white text-sm font-semibold mb-2">Judul Hero</label>
                  <input value={form.hero_title} onChange={(e) => setForm({...form, hero_title: e.target.value})} placeholder="EVENT TEBAK SKOR" data-testid="hero-title-input" />
                </div>
                <div>
                  <label className="block text-white text-sm font-semibold mb-2">Subjudul Hero</label>
                  <textarea rows={2} value={form.hero_subtitle} onChange={(e) => setForm({...form, hero_subtitle: e.target.value})} placeholder="Ikuti setiap Kuis dan menangkan hadiah" data-testid="hero-subtitle-input" />
                </div>
                <LogoUploader
                  value={form.hero_background}
                  onChange={(url) => setForm({...form, hero_background: url})}
                  folder="hero"
                  label="Gambar Latar Hero"
                  dataTestId="hero-bg"
                />
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white text-sm font-semibold mb-2">Teks Tombol Utama</label>
                    <input value={form.primary_button_text} onChange={(e) => setForm({...form, primary_button_text: e.target.value})} placeholder="Mulai Tebak Skor" />
                  </div>
                  <div>
                    <label className="block text-white text-sm font-semibold mb-2">Teks Tombol Sekunder</label>
                    <input value={form.secondary_button_text} onChange={(e) => setForm({...form, secondary_button_text: e.target.value})} placeholder="Jadwal Hari Ini" />
                  </div>
                </div>
                <div>
                  <label className="block text-white text-sm font-semibold mb-2">Tanggal Countdown Event</label>
                  <input type="datetime-local" value={form.countdown_date} onChange={(e) => setForm({...form, countdown_date: e.target.value})} data-testid="countdown-date-input" />
                </div>
              </div>
            </div>

            <div className="glass-card p-6">
              <h2 className="text-xl font-bold text-white mb-4">Section Rundown Pertandingan</h2>
              <p className="text-gray-400 text-sm mb-4">Judul dan sub-judul di atas daftar pertandingan pada homepage.</p>
              <div className="space-y-4">
                <div>
                  <label className="block text-white text-sm font-semibold mb-2">Judul Section</label>
                  <input value={form.rundown_title} onChange={(e) => setForm({...form, rundown_title: e.target.value})} placeholder="Rundown Pertandingan" data-testid="rundown-title-input" />
                </div>
                <div>
                  <label className="block text-white text-sm font-semibold mb-2">Sub-judul Section</label>
                  <input value={form.rundown_subtitle} onChange={(e) => setForm({...form, rundown_subtitle: e.target.value})} placeholder="Pilih pertandingan dan tebak skornya untuk raih hadiah" data-testid="rundown-subtitle-input" />
                </div>
              </div>
            </div>

            <div className="glass-card p-6">
              <h2 className="text-xl font-bold text-white mb-4">Section Partner Resmi (Homepage)</h2>
              <p className="text-gray-400 text-sm mb-4">Judul dan sub-judul untuk section partner di bagian bawah homepage.</p>
              <div className="space-y-4">
                <div>
                  <label className="block text-white text-sm font-semibold mb-2">Judul Section</label>
                  <input value={form.partners_section_title} onChange={(e) => setForm({...form, partners_section_title: e.target.value})} placeholder="PARTNER RESMI EXTRA TIME" data-testid="partners-section-title-input" />
                </div>
                <div>
                  <label className="block text-white text-sm font-semibold mb-2">Sub-judul Section</label>
                  <input value={form.partners_section_subtitle} onChange={(e) => setForm({...form, partners_section_subtitle: e.target.value})} placeholder="Dipercaya oleh brand-brand terbaik" data-testid="partners-section-subtitle-input" />
                </div>
              </div>
            </div>

            <div className="glass-card p-6">
              <h2 className="text-xl font-bold text-white mb-4">Label Statistik Hero</h2>
              <p className="text-gray-400 text-sm mb-4">4 label statistik yang muncul di bawah hero homepage.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white text-sm font-semibold mb-2">Label Pertandingan</label>
                  <input value={form.stat_matches_label} onChange={(e) => setForm({...form, stat_matches_label: e.target.value})} placeholder="Pertandingan Hari Ini" />
                </div>
                <div>
                  <label className="block text-white text-sm font-semibold mb-2">Label Partisipan</label>
                  <input value={form.stat_participants_label} onChange={(e) => setForm({...form, stat_participants_label: e.target.value})} placeholder="Partisipan" />
                </div>
                <div>
                  <label className="block text-white text-sm font-semibold mb-2">Label Prediksi</label>
                  <input value={form.stat_predictions_label} onChange={(e) => setForm({...form, stat_predictions_label: e.target.value})} placeholder="Prediksi" />
                </div>
                <div>
                  <label className="block text-white text-sm font-semibold mb-2">Label Hadiah</label>
                  <input value={form.stat_prizes_label} onChange={(e) => setForm({...form, stat_prizes_label: e.target.value})} placeholder="Hadiah" />
                </div>
                <div>
                  <label className="block text-white text-sm font-semibold mb-2">Angka Hadiah (mis. "100+", "50+", "1000")</label>
                  <input value={form.total_prize_count} onChange={(e) => setForm({...form, total_prize_count: e.target.value})} placeholder="100+" data-testid="total-prize-count-input" />
                </div>
              </div>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <LogoUploader
                  value={form.stat_matches_icon}
                  onChange={(url) => setForm({...form, stat_matches_icon: url})}
                  folder="stats"
                  label="Ikon Card Pertandingan (opsional, fallback Trophy)"
                  dataTestId="stat-matches-icon"
                />
                <LogoUploader
                  value={form.stat_prizes_icon}
                  onChange={(url) => setForm({...form, stat_prizes_icon: url})}
                  folder="stats"
                  label="Ikon Card Hadiah (opsional, fallback Gift)"
                  dataTestId="stat-prizes-icon"
                />
              </div>
            </div>

            <div className="glass-card p-6">
              <h2 className="text-xl font-bold text-white mb-4">Footer</h2>
              <p className="text-gray-400 text-sm mb-4">Kelola konten footer di bagian bawah semua halaman.</p>
              <div className="space-y-4">
                <div>
                  <label className="block text-white text-sm font-semibold mb-2">Deskripsi Singkat (Kolom Kiri Footer)</label>
                  <textarea rows={2} value={form.footer_description} onChange={(e) => setForm({...form, footer_description: e.target.value})} placeholder="Platform prediksi skor sepak bola terpercaya dengan hadiah menarik." data-testid="footer-description-input" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white text-sm font-semibold mb-2">Judul Kolom "Menu Cepat"</label>
                    <input value={form.footer_menu_title} onChange={(e) => setForm({...form, footer_menu_title: e.target.value})} placeholder="Menu Cepat" data-testid="footer-menu-title-input" />
                  </div>
                  <div>
                    <label className="block text-white text-sm font-semibold mb-2">Judul Kolom "Informasi"</label>
                    <input value={form.footer_info_title} onChange={(e) => setForm({...form, footer_info_title: e.target.value})} placeholder="Informasi" data-testid="footer-info-title-input" />
                  </div>
                </div>
                <div>
                  <label className="block text-white text-sm font-semibold mb-2">Copyright / Baris Bawah Footer</label>
                  <input value={form.footer_text} onChange={(e) => setForm({...form, footer_text: e.target.value})} placeholder="© 2025 EXTRA TIME. All rights reserved." data-testid="footer-text-input" />
                </div>
              </div>
            </div>

            <div className="glass-card p-6">
              <h2 className="text-xl font-bold text-white mb-4">Syarat &amp; Ketentuan</h2>
              <p className="text-gray-400 text-sm mb-3">
                Konten yang muncul di modal "Syarat &amp; Ketentuan" pada setiap kartu pertandingan dan halaman <code className="text-[#FFD60A]">/terms</code>.
                Ketik apa adanya — akan ditampilkan persis seperti yang Anda tulis (line break dipertahankan, tanpa format khusus).
              </p>
              <textarea
                value={form.terms_content}
                onChange={(e) => setForm({...form, terms_content: e.target.value})}
                rows={16}
                placeholder="1. Promosi berlaku untuk seluruh member..."
                className="font-mono text-sm"
                data-testid="terms-content-input"
              />
            </div>
          </div>
        )}
      </main>
    </div>
  );
};
